/**
 * agentUi.js — routing agent ui.action envelopes onto the 3D stage, plus the small display
 * helpers the agent components share (CONTRACT §10.2, §10.5).
 *
 * Pure. Generic: no asset ids, no role names. Dashboard supplies labels through props.
 *
 *   routeAgentUiAction(envelope, {activeCaseId, stageAsset}) -> {apply, switchAsset, action}
 *   new ActionQueue()   holds actions until the stage manifest for their asset has loaded
 *   openCasePlan(id, {activeCaseId, lastUiByCase}) -> what opening a case does to the stage
 *   highlightFromAction / liveHighlight   the agent highlight overlay, owned by one case
 *   autoOpenCaseId(cases, {activeCaseId, chosen, tried}) -> the case a fresh agent-mode load opens
 *   createCaseChooser({initialCaseId})   that decision's per-page-load state (useAgent keeps one)
 *   filePreviewKind / fileLinkProps   safe preview and link for a case file (no active content opened)
 *   auditKindLabel / auditKindOptions / filterAuditEntries / auditSubject / spendText
 */

import { validUiAction } from './agentProtocol.js';

/**
 * Decide what the stage does with one agent ui.action.
 * Accepts the Envelope ({type, case_id, data:{action, asset, caption}}) or the flat form the hook
 * passes to onUiAction ({action, asset, case_id, ...}).
 *   - actions of a case other than the active one are not applied (case_id null = voice, applied);
 *   - when the action's asset differs from the stage asset, switchAsset names the asset to load.
 */
export function routeAgentUiAction(envelope, { activeCaseId = null, stageAsset = null } = {}) {
  const env = envelope || {};
  const data = env.data && typeof env.data === 'object' ? env.data : env;
  const action = data.action ?? null;
  const caseId = env.case_id ?? data.case_id ?? null;
  if (!validUiAction(action)) return { apply: false, switchAsset: null, action };
  if (caseId && caseId !== activeCaseId) return { apply: false, switchAsset: null, action };
  const asset = action.type === 'asset' ? action.asset : (data.asset ?? null);
  const switchAsset = asset && asset !== stageAsset ? asset : null;
  return { apply: true, switchAsset, action };
}

/**
 * Opening a case from the rail, a toast or a deep link.
 *   {switch:false}                       already active
 *   {switch:true, caseId, apply, restore}
 *     apply   — {action, meta} of the case's last live ui.action, to re-apply at once
 *     restore — true when the case has not been seen live: fetch its stored last ui.action
 * The caller must make `caseId` the live active case (the hook's ref) BEFORE it applies `apply`, and
 * the stage must route against that live value, not a copy synced after the next render: otherwise
 * routeAgentUiAction sees the previous case and drops the action.
 */
export function openCasePlan(id, { activeCaseId = null, lastUiByCase = {} } = {}) {
  const next = id || null;
  if (next === (activeCaseId || null)) return { switch: false, caseId: next, apply: null, restore: false };
  if (!next) return { switch: true, caseId: null, apply: null, restore: false };
  const lastUi = lastUiByCase?.[next];
  if (!lastUi) return { switch: true, caseId: next, apply: null, restore: true };
  return {
    switch: true,
    caseId: next,
    restore: false,
    apply: {
      action: lastUi.action,
      meta: {
        asset: lastUi.asset ?? null, caption: lastUi.caption ?? null, case_id: next,
        seq: lastUi.seq ?? null, tool_call_id: lastUi.tool_call_id ?? null,
      },
    },
  };
}

/**
 * The agent highlight overlay for one `highlight` ui.action: {ids, roles, ranks, colors, label, seq,
 * caseId, asset}. caseId is the case the action belongs to (meta.case_id, else the active case);
 * asset is the stage asset it paints (the asset being switched to, else the current one).
 */
export function highlightFromAction(action, meta = {}, { activeCaseId = null, asset = null } = {}) {
  if (!action || action.type !== 'highlight') return null;
  return {
    ids: Array.isArray(action.part_ids) ? action.part_ids : [],
    roles: action.roles, ranks: action.ranks, colors: action.colors,
    label: meta.caption ?? null,
    seq: meta.seq ?? null,
    caseId: meta.case_id ?? activeCaseId ?? null,
    asset: asset ?? null,
  };
}

/**
 * The highlight to paint: only the active case's, and only on the asset it was made for (another
 * case's suspects, or part ids of another tree, never stay painted).
 */
export function liveHighlight(highlight, { activeCaseId = null, stageAsset = null } = {}) {
  if (!highlight) return null;
  if ((highlight.caseId ?? null) !== (activeCaseId ?? null)) return null;
  if (highlight.asset && stageAsset && highlight.asset !== stageAsset) return null;
  return highlight;
}

/**
 * Actions waiting for their asset's manifest. The built tree carries no vehicle id and the old
 * tree stays mounted until the new manifest arrives, so readiness is the manifest's `vehicle`
 * field, passed in by the caller: takeReady(stageManifest?.vehicle).
 * Items: {action, asset, seq, caption?, case_id?}. An item without an asset is released by any
 * loaded vehicle. `mapAsset` maps an action asset onto a manifest vehicle id (identity by default).
 */
export class ActionQueue {
  constructor({ mapAsset = (a) => a, max = 50 } = {}) {
    this.items = [];
    this.mapAsset = mapAsset;
    this.max = max;
  }

  push(item) {
    if (!item || !item.action) return;
    this.items.push(item);
    if (this.items.length > this.max) this.items.splice(0, this.items.length - this.max);
  }

  takeReady(loadedVehicle) {
    if (!loadedVehicle) return [];
    const ready = [];
    const waiting = [];
    for (const item of this.items) {
      const want = item.asset ? this.mapAsset(item.asset) : null;
      if (!want || want === loadedVehicle) ready.push(item);
      else waiting.push(item);
    }
    this.items = waiting;
    return ready;
  }

  clear() { this.items = []; }

  get size() { return this.items.length; }
}

/**
 * The items of a workspace action queue ([{action, seq}]) still to apply: integer seq greater than
 * the last consumed one, in queue order. Items without an integer seq are never applied, since
 * nothing could mark them consumed and they would re-apply on every render.
 */
export function pendingQueueItems(queue, consumedSeq = null) {
  if (!Array.isArray(queue)) return [];
  const floor = Number.isInteger(consumedSeq) ? consumedSeq : -Infinity;
  return queue.filter((item) => item && item.action && Number.isInteger(item.seq) && item.seq > floor);
}

/* ------------------------------------------------------------ display */

/** Indian digit grouping, matching contracts.format_inr: 159123 -> "1,59,123", 1150.5 -> "1,150.50". */
export function formatInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const neg = n < 0;
  const cents = Math.round(Math.abs(n) * 100);
  const whole = Math.floor(cents / 100);
  const frac = cents % 100;
  const digits = String(whole);
  let grouped = digits;
  if (digits.length > 3) {
    const tail = digits.slice(-3);
    let head = digits.slice(0, -3);
    const groups = [];
    while (head.length > 2) { groups.unshift(head.slice(-2)); head = head.slice(0, -2); }
    if (head) groups.unshift(head);
    grouped = `${groups.join(',')},${tail}`;
  }
  return `${neg ? '-' : ''}${grouped}${frac ? `.${String(frac).padStart(2, '0')}` : ''}`;
}

/** Seconds -> "m:ss" (or "h:mm:ss" past an hour). */
export function formatElapsed(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Live elapsed seconds of a case: to closed_at when closed, else to `nowMs`. */
export function caseElapsed(caseObj, nowMs) {
  if (!caseObj) return 0;
  const start = Date.parse(caseObj.created_at ?? '');
  if (!Number.isFinite(start)) return Number(caseObj.elapsed_s) || 0;
  const end = caseObj.closed_at ? Date.parse(caseObj.closed_at) : nowMs;
  return Math.max(0, Math.round(((Number.isFinite(end) ? end : nowMs) - start) / 1000));
}

/**
 * Scenario clock for schedule-triggered cases: trigger time plus elapsed, as "HH:MM".
 * The time is read from the ISO string itself (its own offset), never the browser zone.
 */
export function scenarioClockText(caseObj, nowMs) {
  const trig = caseObj?.trigger;
  if (!trig || trig.kind !== 'schedule' || !trig.at) return null;
  const m = /T(\d{2}):(\d{2})/.exec(trig.at);
  if (!m) return null;
  const mins = Number(m[1]) * 60 + Number(m[2]) + Math.floor(caseElapsed(caseObj, nowMs) / 60);
  const hh = String(Math.floor(mins / 60) % 24).padStart(2, '0');
  const mm = String(mins % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Case status -> chip text and tone. Status always shows icon + label, never colour alone. */
export const CASE_STATUS = {
  working: { label: 'Working', tone: 'accent' },
  waiting: { label: 'Waiting for you', tone: 'decide' },
  paused: { label: 'Paused', tone: 'warn' },
  done: { label: 'Done', tone: 'good' },
  cancelled: { label: 'Cancelled', tone: 'faint' },
  failed: { label: 'Failed', tone: 'bad' },
};

export function caseStatusInfo(status) {
  return CASE_STATUS[status] ?? { label: String(status || 'Unknown'), tone: 'faint' };
}

/**
 * "Start line replay" stays disabled while a replay-opened case is still working, or while a start the
 * user just sent has not produced its case yet (`starting`). One replay at a time (2026-10-01): repeated
 * clicks ran several cases at once and NVIDIA rate-limited them. The agent refuses a second replay too;
 * this only keeps the button honest.
 */
export function replayBusy(cases, { starting = false } = {}) {
  if (starting) return true;
  const list = Array.isArray(cases) ? cases : Object.values(cases || {});
  return list.some((c) => c && c.opened_by === 'agent' && c.status === 'working');
}

/**
 * The open case an agent error names ("CASE-0004 from the last replay is still running…"), so the error can
 * offer to close it. Null when the message names no case, or that case is already finished.
 */
export function blockingCaseId(cases, error) {
  const ref = String(error?.message || '').match(/\bCASE-\d+\b/)?.[0];
  if (!ref) return null;
  const list = Array.isArray(cases) ? cases : Object.values(cases || {});
  const hit = list.find((c) => c?.ref === ref && !['done', 'cancelled', 'failed'].includes(c.status));
  return hit?.id ?? null;
}

/** The pulsing dot: the agent opened this case itself and it is not finished. */
export function isAgentOpened(caseObj) {
  return Boolean(caseObj) && caseObj.opened_by === 'agent' && caseObj.status !== 'done';
}

const IMAGE_RE = /\.(png|jpe?g|gif|webp|svg)$/i;
export const isImagePath = (path) => IMAGE_RE.test(String(path || ''));
export const isPdfPath = (path) => /\.pdf$/i.test(String(path || ''));

/*
 * Case files are agent-written and served from the app origin (the dev proxy). A document that can run
 * script there (HTML, SVG, XML/XSL, JS) must never be navigated to: no target=_blank, no unsandboxed
 * iframe. HTML is previewed in a sandboxed iframe from the fetched text (srcdoc; sandbox="" gives it an
 * opaque origin and no scripts, forms, popups or top navigation); SVG only ever renders through <img>,
 * which runs no script. Their links download instead of opening. PNG/JPEG and PDF keep "Open".
 */
const ACTIVE_RE = /\.(html?|xhtml|svg|xml|xslt?|m?js)$/i;
/** True for files that could run script if the browser navigated to them on the app origin. */
export const isActiveContentPath = (path) => ACTIVE_RE.test(String(path || ''));
/** HTML documents: previewed rendered, in the sandboxed iframe. */
export const isHtmlPath = (path) => /\.(html?|xhtml)$/i.test(String(path || ''));
/** The iframe sandbox for HTML previews: no tokens at all (never allow-scripts / allow-same-origin). */
export const PREVIEW_SANDBOX = '';
/** Largest HTML document rendered in the preview; bigger ones are offered as a download only. */
export const HTML_PREVIEW_BYTES = 2 * 1024 * 1024;

/** How a file preview renders: 'image' | 'pdf' | 'html' (sandboxed iframe) | 'text' (fetched, mono). */
export function filePreviewKind(path) {
  if (!path) return null;
  if (isImagePath(path)) return 'image';
  if (isPdfPath(path)) return 'pdf';
  if (isHtmlPath(path)) return 'html';
  return 'text';
}

/**
 * Attributes for a file's link: active content downloads (download attribute, no target), everything
 * else opens in a new tab with noopener. Returns null without a URL.
 */
export function fileLinkProps(path, url) {
  if (!url) return null;
  if (isActiveContentPath(path)) {
    const name = String(path || '').split('/').pop() || 'file';
    return { href: url, download: name, label: 'Download', newTab: false };
  }
  return { href: url, target: '_blank', rel: 'noopener noreferrer', label: 'Open', newTab: true };
}

/* ------------------------------------------------------------ audit */

/** Audit entry kinds (CONTRACT 3.10, plus `serving`: one entry per model call) -> display labels. */
export const AUDIT_KIND_LABELS = {
  tool_call: 'Tool call',
  serving: 'Model call',
  approval: 'Approval',
  memory: 'Memory',
  policy: 'Policy',
  control: 'Control',
  comment: 'Comment',
  draft_op: 'Draft edit',
  handoff: 'Handoff',
  voice: 'Voice',
};

export function auditKindLabel(kind) {
  return AUDIT_KIND_LABELS[kind] ?? String(kind || 'Other');
}

/** Kinds present in the entries, in AUDIT_KIND_LABELS order, unknown kinds after: [{kind, label, count}]. */
export function auditKindOptions(entries) {
  const counts = new Map();
  for (const e of entries || []) {
    const k = e?.kind || 'other';
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  const known = Object.keys(AUDIT_KIND_LABELS);
  const rank = (k) => { const i = known.indexOf(k); return i === -1 ? known.length : i; };
  return [...counts.keys()]
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .map((kind) => ({ kind, label: auditKindLabel(kind), count: counts.get(kind) }));
}

/** Entries of one kind; null / 'all' keeps every entry. */
export function filterAuditEntries(entries, kind = null) {
  const list = Array.isArray(entries) ? entries : [];
  if (!kind || kind === 'all') return list;
  return list.filter((e) => (e?.kind || 'other') === kind);
}

/** Token count of a usage / spend object: `tokens`, else prompt + completion. Null when unknown. */
function tokenCount(u) {
  if (!u || typeof u !== 'object') return null;
  if (Number.isFinite(Number(u.tokens)) && u.tokens !== null && u.tokens !== '') return Number(u.tokens);
  if (Number.isFinite(Number(u.total_tokens)) && u.total_tokens !== null && u.total_tokens !== '') return Number(u.total_tokens);
  const p = Number(u.prompt_tokens);
  const c = Number(u.completion_tokens);
  if (!Number.isFinite(p) && !Number.isFinite(c)) return null;
  return (Number.isFinite(p) ? p : 0) + (Number.isFinite(c) ? c : 0);
}

const groupInt = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** "Tool or event" cell of an audit entry: the tool, the data event, or for a model call its model. */
export function auditSubject(entry) {
  if (!entry) return '';
  if (entry.tool) return entry.tool;
  if (entry.kind === 'serving') {
    const d = entry.data || {};
    const who = [d.target, d.model].filter(Boolean).join(' · ');
    const tokens = tokenCount(d.usage);
    return [who || 'model', tokens !== null ? `${groupInt(tokens)} tokens` : ''].filter(Boolean).join(', ');
  }
  return entry.data?.event || '';
}

/**
 * The model spend of an approval ({calls, prompt_tokens, completion_tokens, tokens[, cost, currency]})
 * as one line, e.g. "4 model calls · 12,340 tokens · 0.42 USD". Null when the approval carries none.
 */
export function spendText(spend, defaultCurrency = null) {
  if (!spend || typeof spend !== 'object') return null;
  const parts = [];
  const calls = Number(spend.calls);
  if (Number.isFinite(calls) && spend.calls !== null) parts.push(`${groupInt(calls)} model call${calls === 1 ? '' : 's'}`);
  const tokens = tokenCount(spend);
  if (tokens !== null) parts.push(`${groupInt(tokens)} tokens`);
  const cost = Number(spend.cost);
  if (spend.cost !== null && spend.cost !== undefined && spend.cost !== '' && Number.isFinite(cost)) {
    const cur = spend.currency || defaultCurrency;
    parts.push(`${cost < 1 ? cost.toFixed(4).replace(/0{1,2}$/, '') : cost.toFixed(2)}${cur ? ` ${cur}` : ''}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

/* ------------------------------------------------------------ case auto-open */

const CLOSED_STATUS = new Set(['done', 'cancelled', 'failed']);
/** A case still being worked: not done / cancelled / failed and not closed. */
export const isOpenCase = (c) => Boolean(c && c.id) && !CLOSED_STATUS.has(c.status) && !c.closed_at;

/**
 * The case a fresh page load opens by itself: the most recently updated open case (updated_at, else
 * created_at), but only when nothing chose a case already — a ?case= deep link, the user, or a voice
 * focus (`chosen`), or an active case — and only once per page load (`tried`). Null otherwise.
 */
export function autoOpenCaseId(cases, { activeCaseId = null, chosen = false, tried = false } = {}) {
  if (tried || chosen || activeCaseId) return null;
  const list = (Array.isArray(cases) ? cases : Object.values(cases || {})).filter(isOpenCase);
  if (list.length === 0) return null;
  const at = (c) => {
    const n = Date.parse(c.updated_at ?? c.created_at ?? '');
    return Number.isFinite(n) ? n : 0;
  };
  list.sort((a, b) => at(b) - at(a) || String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));
  return list[0].id;
}

/**
 * The per-page-load auto-open state useAgent keeps in a ref. `initialCaseId` is the ?case= deep link.
 *   chooser.choose()                                   a user / voice / agent choice (also "none")
 *   chooser.onSnapshot(cases, {autoOpen, activeCaseId}) -> the case id to open, or null; only the first
 *                                                         snapshot of the page load can return one
 */
export function createCaseChooser({ initialCaseId = null } = {}) {
  let chosen = Boolean(initialCaseId);
  let tried = false;
  return {
    choose() { chosen = true; },
    onSnapshot(cases, { autoOpen = false, activeCaseId = null } = {}) {
      const first = !tried;
      tried = true;
      if (!autoOpen || !first) return null;
      return autoOpenCaseId(cases, { activeCaseId, chosen, tried: false });
    },
    get chosen() { return chosen; },
    get tried() { return tried; },
  };
}

/** "outputs/drafts/d_41aa.json" -> "d_41aa", else null. */
export function draftIdFromPath(path) {
  const m = /^outputs\/drafts\/([^/]+)\.json$/.exec(String(path || ''));
  return m ? m[1] : null;
}

/** Zoom range label: "48h" -> "last 48 h", "7d" -> "last 7 d", "all" -> "". */
export function rangeLabel(range) {
  const m = /^(\d+)([hdm])$/.exec(String(range || ''));
  return m ? `last ${m[1]} ${m[2]}` : '';
}

/** First sentence-ish line of a long text, for one-line chips. */
export function oneLine(text, max = 140) {
  const s = String(text ?? '').replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** "2026-10-01T09:30:05+05:30" -> "09:30:05" (the timestamp's own clock). */
export function clockOf(iso) {
  const m = /T(\d{2}:\d{2}:\d{2})/.exec(String(iso || ''));
  return m ? m[1] : '';
}

/** Duration in ms -> "2.1 s" / "340 ms". */
export function formatDuration(ms) {
  if (ms === null || ms === undefined || ms === '') return '';
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '';
  return n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${Math.round(n)} ms`;
}

/** Strip a trailing parenthetical note from a rule description. */
export const ruleText = (desc) => String(desc ?? '').replace(/\s*\([^)]*\)\s*$/, '').trim();
